import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  Dialog,
  DialogContent,
  Snackbar,
  Alert,
  Button,
  Avatar,
  Tabs,
  Tab,
} from "@mui/material";
import axios from "axios";
import Layout from "../../../layout/Layout";
import { useNavigate } from "react-router-dom";
import { hrMasterListAPI } from "../../../config";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import { MaterialReactTable } from "material-react-table";
import EditLocationOutlinedIcon from "@mui/icons-material/EditLocationOutlined";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import { useLocation } from "react-router-dom";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

function ListEmployee() {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const [HrMasterList, setHrMasterList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const location = useLocation();
  // Default to Active employees (present_employee: 2) unless arriving with
  // an explicit filter already set (e.g. from the dashboard's stat cards).
  const [filters, setFilters] = useState(
    location.state?.filters?.length
      ? location.state.filters
      : [{ id: "present_employee", value: 2 }]
  );
  const statusFilter =
    filters.find((f) => f.id === "present_employee")?.value ?? 2;

  const handleStatusTabChange = (event, newValue) => {
    setFilters((prev) => [
      ...prev.filter((f) => f.id !== "present_employee"),
      { id: "present_employee", value: newValue },
    ]);
  };

  const handleExportData = async () => {
    try {
      setLoading(true);

      const payload = {
        pageIndex: 0,
        pageSize: 1000000,
        filters,
        sorting,
      };

      const response = await axios.post(hrMasterListAPI, payload);

      const data = response?.data?.result || [];

      if (data.length === 0) {
        alert("No data to export!");
        return;
      }

      const exportData = data.map((item, index) => ({
        "#": index + 1,
        "Emp. No": item.employee_no || "",
        "Profile Image": item.photo || "",
        Name: item.employee_name || "",
        Email: item.email || "",
        Company: item.company_name || "",
        Position: item.position_name || "",
        "Login Pin": item.pin || "",
        Contact: item.phone || "",
        Status:
          item.present_employee === 2
            ? "Active"
            : item.present_employee === 3
              ? "InActive"
              : "",
        Reason: item.reason_for_relieve || "",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "EmployeeList");

      const fileName = `Employee_list_${moment().format(
        "DD_MM_YYYY"
      )}.xlsx`;
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    const data = {
      pageIndex,
      pageSize,
      filters,
      sorting,
    };
    try {
      const response = await axios.post(hrMasterListAPI, data);
      setHrMasterList(response.data.result || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pageIndex, pageSize, filters, sorting]);

  useEffect(() => {
    if (location.state?.filters) {
      setFilters(location.state.filters);
    }
  }, [location.state]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "employee_no",
        header: "Emp. No",
        enableSorting: true,
        enableFiltering: true,
        size: 100,
      },
      {
        accessorKey: "photo",
        header: "Profile Image",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        muiTableBodyCellProps: {
          sx: {
            textAlign: "center",
            padding: "3px",
            border: "1px solid #ccc",
          },
        },
        Cell: ({ cell, row }) => {
          const employeeName = row.original.employee_name || "User";

          const initials = employeeName
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 1);

          const [open, setOpen] = useState(false);
          const photoUrl = cell.getValue();

          const handleClickOpen = () => {
            setOpen(true);
          };

          const handleClose = () => {
            setOpen(false);
          };

          return (
            <>
              <Avatar
                onClick={handleClickOpen}
                src={photoUrl || undefined}
                alt={employeeName}
                sx={{
                  width: 60,
                  height: 60,
                  margin: "0 auto",
                  cursor: photoUrl ? "pointer" : "default",
                  fontWeight: 600,
                  fontSize: 18,
                  bgcolor: photoUrl ? "transparent" : "#b0bec5", // gray bg if no image
                  color: "#fff",
                  border: "2px solid #ddd",
                }}
              >
                {!photoUrl ? initials || "NA" : null}
              </Avatar>
              {photoUrl && (
                <Dialog open={open} onClose={handleClose} maxWidth="md">
                  <DialogContent
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "20px",
                    }}
                  >
                    <img
                      src={photoUrl}
                      alt="Employee"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "80vh", // Limit the height for better visibility
                        objectFit: "contain",
                      }}
                    />
                  </DialogContent>
                </Dialog>
              )}
            </>
          );
        },
      },
      {
        accessorKey: "employee_name",
        header: "Name",
        enableSorting: true,
        enableFiltering: true,
        size: 200,
      },

      {
        accessorKey: "email",
        header: "Email",
        enableSorting: true,
        enableFiltering: true,
        size: 200,
      },
      {
        accessorKey: "company_name",
        header: "Company",
        enableSorting: false,
        enableFiltering: true,
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: "K.G Build Tech",
            title: "K.G Build Tech",
            value: 20,
          },
          {
            label: "K.G Constructions",
            title: "K.G Constructions",
            value: 21,
          },
          {
            label: "K.G Furn Tech",
            title: "K.G Furn Tech",
            value: 22,
          },
          {
            label: "K.G TradeTech",
            title: "K.G TradeTech",
            value: 23,
          },
        ],
        size: 150,
      },
      {
        accessorKey: "position_name",
        header: "Position",
        enableSorting: true,
        enableFiltering: true,
        size: 150,
      },
      {
        accessorKey: "pin",
        header: "Login Pin",
        enableSorting: true,
        enableFiltering: true,
        muiTableBodyCellProps: {
          sx: {
            textAlign: "center",
            border: "1px solid #ccc",
          },
        },
        Cell: ({ cell }) => (cell.getValue() ? cell.getValue() : "-"),
        size: 90,
      },
      {
        accessorKey: "phone",
        header: "Contact",
        enableSorting: true,
        enableFiltering: true,
        size: 100,
      },
      {
        accessorKey: "present_employee",
        header: "Status",
        Cell: ({ cell }) => {
          const status = cell.getValue();
          const statusText = status === 3 ? "Inactive" : "Active";

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <Typography
                // className={status === 3 ? "inactive-status" : "active-status"}
                sx={{
                  fontSize: "14px",
                  width: "7rem",
                  textAlign: "center",
                  backgroundColor: status === 3 ? "#fae0e4" : "#d8f3dc",
                  color: status === 3 ? "#ff0a54" : "#006400",
                  fontWeight: "bold",
                  padding: "2px 1px",
                  borderRadius: "5px",
                }}
              >
                {statusText}
              </Typography>
            </div>
          );
        },
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: "Active",
            title: "Active",
            value: 2,
          },
          {
            label: "Inactive",
            title: "Inactive",
            value: 3,
          },
        ],
        size: 100,
      },
      {
        accessorKey: "reason_for_relieve",
        header: "Reason",
        Cell: ({ cell }) => (
          <Typography
            // className={cell.getValue() ? "inactive-status" : "active-status"}
            style={{
              fontSize: "14px",
              width: "7rem",
              textAlign: "center",
              color: cell.getValue() ? "#ff0a54" : "#006400",
              fontWeight: "bold",
            }}
          >
            {cell.getValue() || "Present"}
          </Typography>
        ),
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: "Active",
            title: "Active",
            value: "Active",
          },
          {
            label: "Relieved",
            title: "Relieved",
            value: "Relieved",
          },
          {
            label: "Terminated",
            title: "Terminated",
            value: "Terminated",
          },
        ],
        size: 100,
      },
      {
        accessorKey: "ID",
        header: "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <IconButton
            color="warning"
            onClick={() => navigate(`/employee/view/${row.original.ID}`)}
          >
            <EditLocationOutlinedIcon />
          </IconButton>
        ),
        size: 50,
      },
    ],
    [navigate]
  );

  return (
    <Layout>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000} // hide after 3 seconds
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "#28a745",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={6} md={6}>
            <Typography className="page_title">List Employee</Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Employee List</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} textAlign="right">
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              href="/employee/add"
              startIcon={<AddIcon />}
              sx={{ mr: 2 }}
            >
              Add Profile
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <Tabs
          value={statusFilter}
          onChange={handleStatusTabChange}
          sx={{ mb: 2, minHeight: 40, "& .MuiTab-root": { minHeight: 40 } }}
        >
          <Tab label="Active" value={2} />
          <Tab label="Inactive" value={3} />
        </Tabs>

        <MaterialReactTable
          columns={columns}
          data={HrMasterList}
          enableGlobalFilter={false}
          enableStickyHeader
          state={{
            isLoading: loading,
            pagination: { pageIndex, pageSize },
            sorting,
            columnFilters: filters,
          }}
          manualPagination
          enableRowNumbers
          initialState={{
            columnPinning: { right: ["ID"] },
          }}
          manualSorting
          manualFiltering
          rowCount={totalCount}
          onPaginationChange={(updater) => {
            const paginationState = updater({
              pageIndex,
              pageSize,
            });

            const { pageIndex: newPageIndex, pageSize: newPageSize } =
              paginationState;
            setPageIndex(newPageIndex);
            setPageSize(newPageSize);
          }}
          onSortingChange={setSorting}
          onColumnFiltersChange={setFilters}
          muiTableContainerProps={{
            sx: {
              maxHeight: "calc(100vh - 240px)",
              minHeight: 450,
              overflowY: "auto",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              padding: "3px 8px",
              border: "1px solid #ccc",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              padding: "8px",
              border: "1px solid #ccc",
              color: "#fff !important",
              textAlign: "center",
              fontWeight: "bold",
              backgroundColor: "#1976d2",
              "& svg": {
                color: "#fff !important",
              },
              "& .MuiTableSortLabel-root": {
                color: "#fff !important",
                "&.Mui-active": {
                  color: "#fff !important",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
              "& .MuiInputBase-input": {
                color: "#fff !important",
                textFillColor: "#fff !important",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#fff !important",
                opacity: 0.8,
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              borderBottom: "1px solid #ccc",
              backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            },
          })}
          muiTableHeadCellFilterTextFieldProps={{
            sx: {
              "& input": {
                color: "#fff !important", // Change text color
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important", // Change underline color
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important", // Hover underline color
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important", // Focused underline color
              },
            },
          }}
          renderTopToolbarCustomActions={({ table }) => (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: "1rem",
                  p: "0.5rem",
                  justifyContent: "space-between",
                  flexGrow: 1,

                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* RIGHT SIDE BUTTON */}
                  <Tooltip title="Export data to excel sheet" arrow>
                    <Button
                      onClick={handleExportData}
                      style={{ background: "#1a73e829" }}
                      startIcon={<FileDownload />}
                    // startIcon={<IconTableExport />}
                    >
                      Export To Excel
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </>
          )}
        />
      </Grid>
    </Layout>
  );
}

export default ListEmployee;
